import React from 'react'
import { CSSTransition, TransitionGroup } from 'react-transition-group'
import styled, { css } from 'styled-components'
import { useCurrentApplication } from '../store/hooks/uiHooks'
import { useInspirationDetail } from '../store/hooks/uiHooks'
import { isOverviewModuleData } from '../../types/ModuleData'
import { ANIMATION_SCALE, device } from '../style'
import ModuleButton from '../components/ModuleButton'
import ScrollView from '../components/ScrollView'

const ModulesOverviewWrap = styled(TransitionGroup)`
  flex: 1;
  position: relative;
`

const ModulesListInnerWrap = styled.div`
  position: relative;
  display: flex;
  flex-grow: 1;
  flex-shrink: 0;
  width: 100%;
  margin-top: auto;
  margin-bottom: auto;

  @media ${device.laptop} {
    flex-wrap: wrap;
  }
`

const ModulesListWrap = styled(ScrollView)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow-x: auto;
  overflow-y: hidden;

  .simplebar-content {
    min-height: 100%;
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-content: center;
  }

  .simplebar-track {
    display: none;
  }

  @media ${device.laptop} {
    overflow-x: hidden;
    overflow-y: auto;
  }

  &.cs-modules-enter,
  &.cs-modules-appear {
    opacity: 0;

    ${ModuleButton} {
      transition-delay: 0;
      opacity: 0;
      transform: translateY(100%);
    }
  }

  &.cs-modules-enter-active,
  &.cs-modules-appear-active,
  &.cs-modules-enter-done,
  &.cs-modules-appear-done {
    transition: opacity ${1 * ANIMATION_SCALE}s;
    opacity: 1;
    transition-delay: ${1 * ANIMATION_SCALE}s;
    pointer-events: all;

    ${ModuleButton} {
      transition-duration: ${0.5 * ANIMATION_SCALE}s;
      transition-property: opacity, transform;
      opacity: 1;
      transform: translateY(0);
    }
  }

  &.cs-modules-exit {
    opacity: 1;
    transition: opacity ${0.3 * ANIMATION_SCALE}s;
  }
  &.cs-modules-exit-active {
    opacity: 0;
    transition: opacity ${300 * ANIMATION_SCALE}ms;
  }
`

const addEndListener = (node: HTMLElement, done: () => void) => {
  // use the css transitionend event to mark the finish of a transition
  node.addEventListener('transitionend', done, false)
}

const FadeUp = styled.div<{ hide?: boolean }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  max-height: 170px;
  transition-duration: ${0.5 * ANIMATION_SCALE}s;
  transition-property: transform, opacity, visibility;

  @media ${device.laptop} {
    max-height: none;
  }

  ${({ hide }) =>
    hide
      ? css`
          transform: translateY(-40px);
          opacity: 0;
          visibility: hidden;
        `
      : undefined}
`

const ModulesOverview: React.FC = () => {
  const { id: applicationId = '', availableModules = [] } = useCurrentApplication() ?? {}
  const { detailModule } = useInspirationDetail()

  return (
    <FadeUp hide={!!detailModule}>
      <ModulesOverviewWrap appear>
        <CSSTransition key={applicationId} classNames="cs-modules" addEndListener={addEndListener}>
          <ModulesListWrap>
            <ModulesListInnerWrap>
              {availableModules.filter(isOverviewModuleData).map((module, i) => (
                <ModuleButton key={applicationId + '-' + module.id} index={i} module={module} />
              ))}
            </ModulesListInnerWrap>
          </ModulesListWrap>
        </CSSTransition>
      </ModulesOverviewWrap>
    </FadeUp>
  )
}

export default ModulesOverview
